import { useEffect, useState } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { Loader2, Moon, X } from "@fg/icons";
import { connectParityApi, type UserPreferences } from "@/api/connectApi";
import { useAccessToken } from "@/auth/useAccessToken";
import { cn } from "@/lib/utils";

const DAYS = [
    { bit: 1,  label: "Sun" },
    { bit: 2,  label: "Mon" },
    { bit: 4,  label: "Tue" },
    { bit: 8,  label: "Wed" },
    { bit: 16, label: "Thu" },
    { bit: 32, label: "Fri" },
    { bit: 64, label: "Sat" }
];

/**
 * Teams-style "Quiet hours" dialog. Picks a start/end time and the
 * weekdays the schedule applies on; persists to /api/preferences.
 * The BFF push sender skips delivery while the user is inside this
 * window, which gives Connect parity with Teams' Do-Not-Disturb +
 * scheduled quiet hours.
 */
export function QuietHoursDialog({ onClose }: { onClose: () => void }) {
    const token = useAccessToken();
    const qc = useQueryClient();
    const prefs = useQuery({
        queryKey: ["preferences", token],
        queryFn: () => connectParityApi.getPreferences(token),
        enabled: Boolean(token)
    });

    const [enabled, setEnabled] = useState(false);
    const [start, setStart] = useState("22:00");
    const [end, setEnd] = useState("08:00");
    const [daysMask, setDaysMask] = useState(0);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!prefs.data) return;
        const p = prefs.data;
        if (p.dndStartMinutes !== null && p.dndEndMinutes !== null) {
            setEnabled(true);
            setStart(minutesToHHmm(p.dndStartMinutes));
            setEnd(minutesToHHmm(p.dndEndMinutes));
        } else {
            setEnabled(false);
        }
        setDaysMask(p.dndDaysMask ?? 0);
    }, [prefs.data]);

    const save = useMutation({
        mutationFn: (payload: Partial<UserPreferences>) =>
            connectParityApi.upsertPreferences(payload, token),
        onSuccess: (data) => {
            qc.setQueryData(["preferences", token], data);
            onClose();
        }
    });

    const onSave = () => {
        setError(null);
        try {
            const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const payload: Partial<UserPreferences> = enabled
                ? {
                    dndStartMinutes: hhmmToMinutes(start),
                    dndEndMinutes: hhmmToMinutes(end),
                    dndDaysMask: daysMask,
                    timeZoneId: tz
                }
                : {
                    dndStartMinutes: null,
                    dndEndMinutes: null,
                    dndDaysMask: 0,
                    timeZoneId: tz
                };
            save.mutate(payload);
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        }
    };

    return (
        <div
            role="dialog"
            aria-label="Quiet hours"
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={(e) => { if (e.target === e.currentTarget && !save.isPending) onClose(); }}
        >
            <div className="w-full max-w-md rounded-lg border bg-card p-5 shadow-2xl">
                <div className="mb-2 flex items-center gap-2">
                    <Moon className="h-4 w-4 text-primary" aria-hidden />
                    <h2 className="text-base font-semibold">Quiet hours</h2>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        disabled={save.isPending}
                        className="ml-auto rounded p-1 hover:bg-accent disabled:opacity-50"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>
                <p className="mb-4 text-xs text-muted-foreground">
                    Notifications won't push to your devices during the hours below.
                    You'll still see them when you open Connect.
                </p>

                {prefs.isPending ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" /> Loading preferences…
                    </div>
                ) : (
                    <>
                        <label className="mb-3 flex items-center gap-2 text-sm">
                            <input
                                type="checkbox"
                                checked={enabled}
                                onChange={(e) => setEnabled(e.target.checked)}
                                className="h-4 w-4"
                            />
                            Enable quiet hours
                        </label>

                        <fieldset disabled={!enabled} className="space-y-3 disabled:opacity-50">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="mb-1 block text-xs font-medium">From</label>
                                    <input
                                        type="time"
                                        value={start}
                                        onChange={(e) => setStart(e.target.value)}
                                        className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                                    />
                                </div>
                                <div>
                                    <label className="mb-1 block text-xs font-medium">To</label>
                                    <input
                                        type="time"
                                        value={end}
                                        onChange={(e) => setEnd(e.target.value)}
                                        className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="mb-1 block text-xs font-medium">Days</label>
                                <div className="flex flex-wrap gap-1">
                                    {DAYS.map((d) => {
                                        const active = (daysMask & d.bit) !== 0 || daysMask === 0;
                                        return (
                                            <button
                                                key={d.bit}
                                                type="button"
                                                onClick={() => {
                                                    if (daysMask === 0) {
                                                        // Switching from "every day" → restrict; toggle off the picked one.
                                                        setDaysMask(0x7F & ~d.bit);
                                                    } else {
                                                        const next = daysMask ^ d.bit;
                                                        setDaysMask(next === 0x7F ? 0 : next);
                                                    }
                                                }}
                                                className={cn(
                                                    "rounded-full border px-2.5 py-1 text-xs",
                                                    active
                                                        ? "border-primary bg-primary text-primary-foreground"
                                                        : "border-border text-muted-foreground hover:bg-accent"
                                                )}
                                            >
                                                {d.label}
                                            </button>
                                        );
                                    })}
                                </div>
                                <p className="mt-1 text-[10px] text-muted-foreground">
                                    {daysMask === 0 ? "Every day" : "Selected days only"}
                                </p>
                            </div>
                        </fieldset>
                    </>
                )}

                {error ? (
                    <p className="mt-3 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
                        {error}
                    </p>
                ) : null}

                <div className="mt-4 flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={save.isPending}
                        className="rounded-md border px-3 py-1.5 text-sm hover:bg-accent disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={onSave}
                        disabled={save.isPending}
                        className="flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                    >
                        {save.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                        Save
                    </button>
                </div>
            </div>
        </div>
    );
}

function minutesToHHmm(m: number): string {
    const h = Math.floor(m / 60);
    const mm = m % 60;
    return `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}

function hhmmToMinutes(hhmm: string): number {
    const [h, m] = hhmm.split(":").map((s) => parseInt(s, 10) || 0);
    return Math.max(0, Math.min(1439, (h ?? 0) * 60 + (m ?? 0)));
}
